import baseFetch, { createParamString } from 'common/baseFetch';
import type { Camera } from 'types/camera';
import type { ForecastConditions, ForecastWaveHeight, ForecastWind, SwellPoint } from 'types/spot';
import type { Units } from 'types/user';

export interface SpotRecommendation {
  _id: string;
  name: string;
  rank: number;
  lat: number;
  lon: number;
  subregionId: string;
  cameras: Array<Camera>;
  conditions: ForecastConditions | null;
  waveHeight: ForecastWaveHeight;
  wind: ForecastWind;
  swells: Array<SwellPoint>;
  relivableRating: number | null;
  abilityLevels: Array<string>;
  hasLiveWind: boolean;
  timezone?: string;
}

export interface SpotRecommendationsResponse {
  recommendations: Array<SpotRecommendation>;
}

export type SpotRecommendationsUnits = Pick<
  Units,
  'surfHeight' | 'swellHeight' | 'windSpeed' | 'temperature' | 'tideHeight'
>;

export interface FetchSpotRecommendationsParams {
  lat: number;
  lon: number;
  subregionId?: string;
  offset: number;
  limit: number;
  units?: SpotRecommendationsUnits;
}

export const buildSpotRecommendationsQueryParams = ({
  lat,
  lon,
  subregionId,
  offset,
  limit,
  units,
}: FetchSpotRecommendationsParams): Record<string, string | number | null | undefined> => ({
  lat,
  lon,
  subregionId,
  offset,
  limit,
  'units[waveHeight]': units?.surfHeight ?? null,
  'units[swellHeight]': units?.swellHeight ?? null,
  'units[windSpeed]': units?.windSpeed ?? null,
  'units[temperature]': units?.temperature ?? null,
  'units[tideHeight]': units?.tideHeight ?? null,
});

export const getSpotRecommendationsUnitsKey = (units?: SpotRecommendationsUnits) =>
  units
    ? {
        waveHeight: units.surfHeight,
        swellHeight: units.swellHeight,
        windSpeed: units.windSpeed,
        temperature: units.temperature,
        tideHeight: units.tideHeight,
      }
    : null;

export const fetchSpotRecommendations = (
  params: FetchSpotRecommendationsParams,
): Promise<SpotRecommendationsResponse> => {
  const paramString = createParamString(buildSpotRecommendationsQueryParams(params));
  return baseFetch<SpotRecommendationsResponse>(`/kbyg/spots/recommendations?${paramString}`);
};

export const postSpotRecommendationsSeen = (spotIds: Array<string>): Promise<void> =>
  baseFetch('/kbyg/spots/recommendations/seen', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ spotIds }),
  });
